import { useState, useMemo, useCallback } from "react";
import { Loader2, Lock, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { dictionary, Language } from "@/utils/dictionary";
import type { VideoStyle } from "../types/videoStudio.types";
import { VIDEO_STYLES } from "../types/videoStudio.types";

const PRIMARY_VIDEO_STYLE_ID = "product-modeling-voiceover";

interface VideoStudioGalleryProps {
  selectedStyleId: string | null;
  onSelectStyle: (style: VideoStyle) => void;
  onContinue?: () => void;
  canContinue?: boolean;
}

export function VideoStudioGallery({
  selectedStyleId,
  onSelectStyle,
  onContinue,
  canContinue = false,
}: VideoStudioGalleryProps) {
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const { language } = useAuth();

  const handleImageLoad = useCallback((id: string) => {
    setLoadedImages((prev) => new Set(prev).add(id));
  }, []);

  const handleImageError = useCallback((id: string) => {
    setFailedImages((prev) => new Set(prev).add(id));
  }, []);

  const filteredStyles = useMemo(() => {
    return VIDEO_STYLES.filter((s) => s.enabled && s.id === PRIMARY_VIDEO_STYLE_ID);
  }, []);

  return (
    <div className="flex h-full min-h-0 items-center justify-center overflow-y-auto px-6 py-5">
      <div className="flex w-fit flex-col items-center">
        {filteredStyles.map((style) => {
          const isSelected = selectedStyleId === style.id;
          const isDisabled = !style.enabled;
          const canToggle = !isDisabled;
          const imageFailed = failedImages.has(style.id);
          const showThumbnail = !!style.thumbnail && !isDisabled && !imageFailed;
          const isVideoThumbnail = style.thumbnailType === "video" || style.thumbnail?.toLowerCase().endsWith(".mp4");

          return (
            <div
              key={style.id}
              role="button"
              tabIndex={0}
              onClick={() => canToggle && onSelectStyle(style)}
              onKeyDown={(e) => e.key === "Enter" && canToggle && onSelectStyle(style)}
              className={cn(
                "group relative bg-transparent p-0 transition-all duration-200",
                isSelected
                  ? ""
                : canToggle
                    ? "cursor-pointer"
                    : "cursor-not-allowed opacity-40"
              )}
            >
              <div className="flex items-center justify-center gap-6">
                <div
                  className={cn(
                    "relative flex aspect-[9/16] w-[156px] flex-shrink-0 flex-col items-center justify-center overflow-hidden rounded-2xl shadow-sm ring-1 ring-slate-200 sm:w-[166px]",
                    isDisabled
                      ? "bg-gray-200"
                      : !showThumbnail
                        ? "bg-gradient-to-br from-[#1a1a3e] to-[#2d1b69]"
                        : ""
                  )}
                >
                  {showThumbnail && (
                    <>
                      {!loadedImages.has(style.id) && (
                        <div className="absolute inset-0 overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-slate-100">
                          <div className="absolute inset-y-0 -left-1/2 w-1/2 skew-x-[-18deg] bg-white/70 blur-xl animate-shine" />
                          {isVideoThumbnail && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-indigo-600">
                              <Loader2 className="h-5 w-5 animate-spin" />
                            </div>
                          )}
                        </div>
                      )}
                      {isVideoThumbnail ? (
                        <video
                          src={style.thumbnail}
                          className={cn(
                            "absolute inset-0 h-full w-full object-cover transition-opacity duration-300",
                            loadedImages.has(style.id) ? "opacity-100" : "opacity-0"
                          )}
                          muted
                          loop
                          playsInline
                          autoPlay
                          preload="metadata"
                          onLoadedData={() => handleImageLoad(style.id)}
                          onCanPlay={() => handleImageLoad(style.id)}
                          onError={() => handleImageError(style.id)}
                        />
                      ) : (
                        <img
                          src={style.thumbnail}
                          alt={dictionary[style.nameKey]?.[language as Language] ?? style.nameKey}
                          className={cn(
                            "absolute inset-0 h-full w-full object-cover transition-opacity duration-300",
                            loadedImages.has(style.id) ? "opacity-100" : "opacity-0"
                          )}
                          onLoad={() => handleImageLoad(style.id)}
                          onError={() => handleImageError(style.id)}
                        />
                      )}
                    </>
                  )}
                  {isDisabled ? (
                    <Lock className="h-8 w-8 text-gray-400" />
                  ) : !showThumbnail ? (
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-white/85 ring-1 ring-white/20">
                      <Play className="h-6 w-6" />
                    </div>
                  ) : null}

                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/85 via-black/35 to-transparent" />
                </div>

                <div className="flex min-w-0 flex-1 flex-col items-start justify-center">
                  <div>
                    <p className="text-base font-semibold leading-tight text-slate-950">
                      UGC Voz en Off
                    </p>
                    {style.estimated_time ? (
                      <span className="mt-2 inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">
                        {style.estimated_time}
                      </span>
                    ) : null}
                  </div>
                  {onContinue ? (
                    <button
                      type="button"
                      disabled={!canContinue}
                      onClick={onContinue}
                      className={cn(
                        "mt-8 min-w-[144px] rounded-xl px-4 py-2.5 text-sm font-semibold shadow-lg shadow-indigo-100 transition-all",
                        canContinue
                          ? "bg-fluxi text-white hover:-translate-y-0.5 hover:bg-fluxi/95"
                          : "cursor-not-allowed bg-indigo-200 text-white"
                      )}
                    >
                      Continuar
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredStyles.length === 0 && (
        <div className="flex items-center justify-center h-32 text-muted-foreground">
          <p className="text-sm">{dictionary["video-no-styles-found"][language as Language]}</p>
        </div>
      )}
    </div>
  );
}
